import React from 'react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

// Import MarketStatistics from utils (single source of truth)
import { MarketStatistics } from '../../utils/market-statistics'

// Re-export for backward compatibility
export type { MarketStatistics }

interface MarketOverviewPageProps {
  manufacturer?: string | null  // Optional, not displayed
  makeName: string
  modelName: string
  statistics: MarketStatistics
  /** When true, this page acts as the cover page with logo and "Prepared for" text */
  isCoverPage?: boolean
  /** Path to the logo image (required when isCoverPage is true) */
  logoSrc?: string
  /** "Prepared for" text (required when isCoverPage is true) */
  preparedFor?: string
  /** Date string for the cover page header */
  dateString?: string
}

// ====== SVG ICONS (Sized for 80px tile) ======
const DollarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-12 h-12">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 6v12M15 9.5c0-1.5-1.5-2.5-3-2.5s-3 1-3 2.5 1.5 2 3 2.5 3 1 3 2.5-1.5 2.5-3 2.5-3-1-3-2.5" />
  </svg>
)

const HashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-12 h-12">
    <circle cx="12" cy="12" r="9" />
    <path d="M10 6v12M14 6v12M6 10h12M6 14h12" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="w-12 h-12">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
    {/* Hour markers */}
    <circle cx="12" cy="5" r="0.5" fill="currentColor" />
    <circle cx="12" cy="19" r="0.5" fill="currentColor" />
    <circle cx="5" cy="12" r="0.5" fill="currentColor" />
    <circle cx="19" cy="12" r="0.5" fill="currentColor" />
  </svg>
)

// ====== CARD ACCENT LINES (diagonal lines extending from bottom-left of card, going down-left) ======
const CardAccentLines = () => (
  <div className="absolute" style={{ left: -15, bottom: -25, zIndex: 0 }}>
    {/* Lines extending from bottom-left of the card, going down and to the left (like \\) */}
    <div
      className="absolute"
      style={{
        width: 6,
        height: 80,
        backgroundColor: '#fdba74',
        transform: 'rotate(-55deg)',
        transformOrigin: 'top left',
        top: 0,
        left: 0
      }}
    />
    <div
      className="absolute"
      style={{
        width: 4,
        height: 60,
        backgroundColor: '#fdba74',
        transform: 'rotate(-55deg)',
        transformOrigin: 'top left',
        top: 0,
        left: 15
      }}
    />
  </div>
)

// ====== STAT SECTION COMPONENT ======
interface StatSectionProps {
  icon: React.ReactNode
  title: string
  paragraph: string
}

const StatSection = ({ icon, title, paragraph }: StatSectionProps) => (
  <div className="relative flex items-start gap-5">
    {/* Icon Tile with accent lines behind it */}
    <div className="relative flex-shrink-0">
      <CardAccentLines />
      {/* Icon Tile - 80px purple square with rounded corners */}
      <div
        className="relative z-10 flex items-center justify-center text-white"
        style={{
          width: 80,
          height: 80,
          backgroundColor: '#3b0764',
          borderRadius: 8
        }}
      >
        {icon}
      </div>
    </div>
    {/* Content */}
    <div className="flex-1 pt-1">
      {/* Underlined Section Heading */}
      <h3
        style={{
          color: '#3b0764',
          fontFamily: '"Exo 2", sans-serif',
          fontSize: 18,
          fontWeight: 600,
          textDecoration: 'underline',
          textUnderlineOffset: 4,
          marginBottom: 8
        }}
      >
        {title}:
      </h3>
      {/* Descriptive Paragraph */}
      <p
        style={{
          color: '#374151',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: 14,
          lineHeight: 1.5,
          maxWidth: 320
        }}
      >
        {paragraph}
      </p>
    </div>
  </div>
)

// ====== FORMAT HELPERS ======
const formatPriceValue = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`
  }
  return `$${value.toLocaleString()}`
}

// ====== MAIN COMPONENT ======
const MarketOverviewPage: React.FC<MarketOverviewPageProps> = ({
  makeName,
  modelName,
  statistics,
  isCoverPage = false,
  logoSrc,
  preparedFor,
  dateString: _dateString
}) => {
  const { averageSalePrice, numberOfSales, averageTimeOnMarket, historicalMonths, monthlyData = [] } = statistics

  // Generate sentence-form paragraphs with embedded values
  const pricingParagraph = averageSalePrice !== null
    ? `Over the last ${historicalMonths} months, the average sale price of a ${makeName} ${modelName} was ${formatPriceValue(averageSalePrice)} USD.`
    : `No sale price data is available for the past ${historicalMonths} months.`

  const salesParagraph = numberOfSales > 0
    ? `There have been ${numberOfSales} ${makeName} ${modelName}s sold in the past ${historicalMonths} months.`
    : `No ${makeName} ${modelName}s have been sold in the past ${historicalMonths} months.`

  const timeOnMarketParagraph = averageTimeOnMarket !== null
    ? `The average time spent on-market for a ${makeName} ${modelName} over the past ${historicalMonths} months was ${Math.round(averageTimeOnMarket)} days.`
    : `Insufficient data to calculate average time on market for the past ${historicalMonths} months.`

  // Check if we have chart data
  const hasChartData = monthlyData.length > 0 && monthlyData.some(d => d.listingsCount > 0 || d.soldCount > 0)

  return (
    <div
      className={`ij-market-overview-page relative overflow-hidden ${isCoverPage ? 'ij-cover-page' : ''}`}
      style={{
        width: '14in',
        minHeight: '8.5in',
        backgroundColor: '#ffffff',
        pageBreakAfter: 'always'
      }}
    >
      {/* ========== COVER PAGE HEADER (Top-Left Logo + Prepared For) ========== */}
      {isCoverPage && logoSrc && (
        <>
          <img
            src={logoSrc}
            alt="IntelliJet International"
            className="absolute z-20"
            style={{
              top: '16px',
              left: '24px',
              width: '320px',
              height: 'auto'
            }}
          />
          <div
            className="absolute z-20 font-bold"
            style={{
              top: '147px',
              left: '44px',
              color: '#000000',
              fontSize: '14px'
            }}
          >
            Prepared for: {preparedFor || ''}
          </div>
        </>
      )}
      {/* ========== BOTTOM-LEFT: Large diagonal stripes like \\\\ ========== */}
      <div className="absolute bottom-0 left-0 z-0" style={{ width: 500, height: 400 }}>
        {/* Big bold stripes going up-right */}
        <div className="absolute" style={{ width: 20, height: 500, bottom: -100, left: -30, backgroundColor: '#3b0764', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 12, height: 450, bottom: -80, left: 20, backgroundColor: '#fdba74', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 20, height: 480, bottom: -90, left: 55, backgroundColor: '#3b0764', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 10, height: 400, bottom: -60, left: 100, backgroundColor: '#fdba74', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 16, height: 420, bottom: -70, left: 135, backgroundColor: '#3b0764', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 8, height: 350, bottom: -50, left: 175, backgroundColor: '#fdba74', transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 12, height: 380, bottom: -55, left: 205, backgroundColor: '#3b0764', opacity: 0.8, transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
        <div className="absolute" style={{ width: 6, height: 300, bottom: -40, left: 240, backgroundColor: '#fdba74', opacity: 0.7, transform: 'rotate(-55deg)', transformOrigin: 'bottom left' }} />
      </div>

      {/* ========== MAIN CONTENT ========== */}
      <div className="relative z-10" style={{ padding: '48px 80px 48px 120px' }}>

        {/* Header Block - Right-Aligned */}
        <div className="flex justify-end" style={{ marginBottom: 32 }}>
          <div className="text-right">
            {/* Market Overview Title - Underlined */}
            <h1
              style={{
                fontSize: 42,
                fontWeight: 700,
                color: '#3b0764',
                fontFamily: '"Exo 2", sans-serif',
                textDecoration: 'underline',
                textUnderlineOffset: 6,
                marginBottom: 16
              }}
            >
              Market Overview
            </h1>
            {/* Model info */}
            <div style={{ fontFamily: '"Exo 2", sans-serif', fontSize: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginBottom: 4 }}>
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Make:</span>
                <span style={{ color: '#3b0764', fontWeight: 600, minWidth: 120, textAlign: 'right' }}>{makeName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24 }}>
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Model:</span>
                <span style={{ color: '#3b0764', fontWeight: 600, minWidth: 120, textAlign: 'right' }}>{modelName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Column Layout: Stat Cards on Left, Chart on Right */}
        <div className="flex gap-8" style={{ marginTop: 16 }}>

          {/* Left Column - Stat Cards (Left-Justified) */}
          <div className="flex-shrink-0" style={{ width: '420px' }}>
            <div style={{ marginBottom: 48 }}>
              <StatSection
                icon={<DollarIcon />}
                title="Aircraft Pricing"
                paragraph={pricingParagraph}
              />
            </div>

            <div style={{ marginBottom: 48 }}>
              <StatSection
                icon={<HashIcon />}
                title="Number of Sales"
                paragraph={salesParagraph}
              />
            </div>

            <div>
              <StatSection
                icon={<ClockIcon />}
                title="Time on Market"
                paragraph={timeOnMarketParagraph}
              />
            </div>
          </div>

          {/* Right Column - Chart and Reserved Space for Paragraph */}
          <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
            {/* Chart Section */}
            {hasChartData && (
              <div style={{ marginBottom: 24 }}>
                <h3
                  style={{
                    color: '#3b0764',
                    fontFamily: '"Exo 2", sans-serif',
                    fontSize: 18,
                    fontWeight: 600,
                    marginBottom: 16,
                    textAlign: 'center'
                  }}
                >
                  Market Activity ({historicalMonths} months)
                </h3>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={monthlyData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 4,
                          fontSize: 12
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                      />
                      <Bar
                        dataKey="listingsCount"
                        name="For Sale"
                        fill="#3b0764"
                        radius={[4, 4, 0, 0]}
                      />
                      <Line
                        type="monotone"
                        dataKey="soldCount"
                        name="Sold"
                        stroke="#fdba74"
                        strokeWidth={3}
                        dot={{ fill: '#fdba74', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#fdba74', strokeWidth: 2 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Reserved space for future paragraph at bottom right */}
            <div className="flex-1" style={{ minHeight: 80 }}>
              {/* Placeholder for future content */}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MarketOverviewPage

